import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  viewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import Chart from "chart.js/auto";
import type { ChartConfiguration } from "chart.js";
import { Widget } from "../../models/dashboard.model";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { SharedModule } from "../../../../shared/shared.module";
import { UiFacadeService } from "../../../user_infos/services/store/ui-facade.service";
import { ArticlesInfosFacadeService } from "../../../articles/services/article-infos/article-infos-facade.service";
import { parseNumber } from "../../../../shared/utils/number-utils.service";
import { AutoSignificantDigitsPipe } from "../../../../shared/pipes/number-sig.pipe";
import { LoaderSkeletonComponent } from "../../../../shared/loader-skeleton/loader-skeleton.component";
import { OF } from "../../../of/models/of.model";

@Component({
  selector: "vl-widget-articles-produits-user-infos",
  standalone: true,
  imports: [CommonModule, WidgetTitleComponent, SharedModule, AutoSignificantDigitsPipe, LoaderSkeletonComponent],
  templateUrl: "./widget-articles-produits-user-infos.component.html",
  styleUrls: ["./widget-articles-produits-user-infos.component.scss"],
  host: { class: "widget-component" },
})
export class WidgetArticlesProduitsUserInfosComponent {
  widget = input<Widget>();
  metadata = input<EntityMetadata>();
  editable = input(true);

  private uiFacade = inject(UiFacadeService);
  private articleInfosFacade = inject(ArticlesInfosFacadeService);
  stats = this.uiFacade.getState().stats;

  canvasRef = viewChild<ElementRef<HTMLCanvasElement>>("articlesChart");
  chart?: Chart<"doughnut", number[], string>;

  chartData = computed(() => {
    const ofs: OF[] = this.stats()?.ofs ?? [];
    if (!ofs.length) return { labels: [], values: [], colors: [] };

    const grouped = new Map<string, { denom: string; qty: number }>();
    for (const of of ofs) {
      const articleId = of.articleId;
      if (!articleId) continue;
      const entry = grouped.get(articleId) ?? {
        denom:
          this.articleInfosFacade.getByArticleId(articleId)?.()?.denomination ??
          (of as any).article?.denomination ??
          articleId,
        qty: 0,
      };
      entry.qty += parseNumber(of.quantiteProduite ?? 0);
      grouped.set(articleId, entry);
    }

    const entries = [...grouped.values()]
      .sort((a, b) => b.qty - a.qty)
      .slice(0, 12);

    const colors = this.makeColors(entries.length);

    return {
      labels: entries.map((e) => e.denom),
      values: entries.map((e) => e.qty),
      colors,
    };
  });

  legendLabels = computed(() => {
    const data = this.chartData();
    if (!data.labels.length) return null;
    const total = data.values.reduce((a, b) => a + b, 0);
    return data.labels.map((lbl, i) => ({
      label: lbl,
      percentage: total > 0 ? ((data.values[i] / total) * 100).toFixed(0) : "0",
      color: data.colors[i] ?? "#999",
    }));
  });

  private makeColors(n: number): string[] {
    const base = [210, 140, 260, 35, 170, 0, 195, 310];
    const out: string[] = [];
    for (let i = 0; i < n; i++) {
      const h =
        base[i % base.length] +
        (i >= base.length ? 12 * Math.floor(i / base.length) : 0);
      out.push(`hsl(${h % 360} 75% 55%)`);
    }
    return out;
  }

  constructor() {
    effect(() => {
      const canvas = this.canvasRef();
      const data = this.chartData();
      if (!canvas || !data.labels.length) {
        this.destroyChart();
        return;
      }
      this.buildChart(data);
    });
  }

  private destroyChart() {
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }

  private buildChart(data: { labels: string[]; values: number[]; colors: string[] }) {
    const canvas = this.canvasRef();
    if (!canvas) return;
    const ctx = canvas.nativeElement.getContext("2d");
    if (!ctx) return;
    this.destroyChart();

    const config: ChartConfiguration<"doughnut", number[], string> = {
      type: "doughnut",
      data: {
        labels: data.labels,
        datasets: [
          {
            label: "Quantité",
            data: data.values,
            backgroundColor: data.colors,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        animation: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) =>
                ` ${ctx.label}: ${(ctx.parsed as number).toLocaleString("fr-FR")}`,
            },
          },
        },
      },
    };
    this.chart = new Chart<"doughnut", number[], string>(ctx, config);
  }
}
